import React, { useState } from "react"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Badge from "aldoo-ra/Badge"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import { api } from "aldoo-ra/API"
import UserPlatformAPI from "aldoo-ra/Admin/PlatformAccess/user-api"
import { url } from "aldoo-ra/CMS/url"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import { TrashIcon, Cog6ToothIcon } from "@heroicons/react/24/outline"
import CMSData from "aldoo-ra/CMS/cms-data"
import ContentEditor from "aldoo-ra/CMS/content-editor"
import DatabaseModelTypeResolver from "aldoo-ra/CMS/database-model-type-resolver"
import Controller from "aldoo-ra/CMS/controllers/cms-controller"
import StoreProductInfoDialog from "aldoo-ra/Admin/Store/Products/StoreProductInfoDialog"
import UserInfoDrawer from "aldoo-ra/Admin/PlatformAccess/UserInfoDrawer"

export default function FeedbackBrowser() {
  const { getType } = DatabaseModelTypeResolver()
  const controller = Controller()
  const [refreshKey, setRefreshKey] = useState(0)
  const [categoryFilter, setCategoryFilter] = useState([])
  const [userFilter, setUserFilter] = useState(null)
  const [showReasonsDialog, setShowReasonsDialog] = useState(false)
  const [reasonsInstance, setReasonsInstance] = useState(null)
  const [selectedProductPid, setSelectedProductPid] = useState(null)
  const [showProductInfoDialog, setShowProductInfoDialog] = useState(false)
  const [userDetailsModal, setUserDetailsModal] = useState({ isOpen: false, user: null })

  // Fetch unsubscribe reasons using CRUD API (not CMSData) to get raw data without transformations
  const [unsubscribeReasonsData, setUnsubscribeReasonsData] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  const fetchReasons = React.useCallback(async () => {
    try {
      setLoading(true)
      const crudAPI = api("crud")
      const response = await crudAPI({
        get: {
          model: "CMSContent",
          match: { "content.type": "UnsubscribeReasons", deleted: { $ne: true } }
        }
      })

      // CRUD returns array directly
      const data = Array.isArray(response) ? response : []
      setUnsubscribeReasonsData(data)
    } catch (err) {
      console.error('Failed to fetch UnsubscribeReasons:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    fetchReasons()
  }, [])

  const getCategoryFilter = () => {
    if (!categoryFilter || !categoryFilter.length) return { $exists: true }
    return { $in: categoryFilter.map((t) => t.value) }
  }

  const getUserFilter = () => {
    if (!userFilter || !userFilter.length) return { $exists: true }
    const selectedUser = userFilter[0]
    return selectedUser._id
  }

  const handleProductClick = (pid) => {
    setSelectedProductPid(pid)
    setShowProductInfoDialog(true)
  }

  const customGetMethod = async (requestParams) => {
    try {
      const crudAPI = api("crud")

      const finalParams = {
        ...requestParams,
        match: {
          ...requestParams.match,
          'content.category': getCategoryFilter(),
          'content.user': getUserFilter(),
          'content.type': 'UnsubscribeFeedback',
          deleted: { $ne: true }
        }
      }

      const response = await crudAPI({
        get: {
          model: "CMSContent",
          ...finalParams
        }
      })

      // CRUD API returns {data: [], totalItems: N, totalPages: N}
      if (response && Array.isArray(response.data)) {
        // Get unique user IDs (ensure they're strings)
        const userIds = [...new Set(response.data.map(item => {
          const userId = item.content?.user
          return userId ? String(userId) : null
        }).filter(Boolean))]

        // Fetch user data
        let usersMap = {}
        if (userIds.length > 0) {
          const usersResponse = await crudAPI({
            get: {
              model: "User",
              match: { _id: { $in: userIds } }
            }
          })

          // Handle both array and {data: []} response formats
          const users = Array.isArray(usersResponse) ? usersResponse : (usersResponse?.data || [])
          users.forEach(user => {
            usersMap[String(user._id)] = user
          })
        }

        // Fetch products to resolve product names
        const productIds = [...new Set(response.data.map(item => item.content?.product_id).filter(Boolean))]
        let productsMap = {}

        if (productIds.length > 0) {
          const productsResponse = await crudAPI({
            get: {
              model: "StoreProduct",
              match: { pid: { $in: productIds } }
            }
          })

          const products = Array.isArray(productsResponse) ? productsResponse : (productsResponse?.data || [])
          products.forEach(product => {
            productsMap[product.pid] = product
          })
        }

        const transformedData = response.data.map(item => {
          const userId = String(item.content?.user)
          const productId = item.content?.product_id
          const product = productsMap[productId]

          return {
            _id: item._id,
            user: usersMap[userId] || null,
            product_name: product?.name || item.content?.product_id || "Unknown",
            product_id: item.content?.product_id,
            category: item.content?.category,
            user_notes: item.content?.user_notes,
            feedback_date: item.content?.feedback_date,
            createdAt: item.createdAt
          }
        })

        return {
          result: true,
          data: transformedData,
          total: response.totalItems || response.data.length
        }
      }

      return { result: true, data: [], total: 0 }
    } catch (error) {
      console.error('Error fetching feedback data:', error)
      return { result: false, data: [], total: 0 }
    }
  }

  const categoryOptions = [
    { value: "The price is too high", name: "The price is too high" },
    { value: "I'm having technical difficulties", name: "I'm having technical difficulties" },
    { value: "I don't like the product", name: "I don't like the product" },
    { value: "I don't want to be auto-billed", name: "I don't want to be auto-billed" },
    { value: "I no longer need it", name: "I no longer need it" },
    { value: "Other", name: "Other" }
  ]

  const formatDate = (date) => {
    if (!date) return "N/A"
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const handleDelete = async (item) => {
    showPromptDialog({
      title: "Delete Feedback",
      message: "Are you sure you want to delete this feedback entry?",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const crudAPI = api("crud")
          await crudAPI({
            delete: {
              model: "CMSContent",
              match: { _id: item._id },
              mode: "hard"
            }
          })

          showNotification({
            message: "Feedback deleted successfully",
            className: "bg-admin_primary text-admin_text_inv"
          })

          setRefreshKey((prev) => prev + 1)
        } catch (error) {
          console.error("Failed to delete feedback:", error)
          showNotification({
            message: "Failed to delete feedback",
            className: "bg-admin_error text-admin_text_inv"
          })
        }
      }
    })
  }

  const handleOpenReasonsDialog = () => {
    const rawData = unsubscribeReasonsData.length > 0 ? unsubscribeReasonsData[0] : null

    if (!rawData) {
      showNotification({
        message: "No reasons data found. Please check the database.",
        className: "bg-admin_error text-admin_text_inv"
      })
      return
    }

    const contentType = getType("UnsubscribeReasons")

    if (!contentType) {
      showNotification({
        message: "Content type not found. Please refresh the page.",
        className: "bg-admin_error text-admin_text_inv"
      })
      return
    }

    setReasonsInstance(rawData)
    setShowReasonsDialog(true)
  }


  const feedbackItemRender = [
    {
      id: "feedback_date",
      title: "Date",
      headerClassName: "w-[120px]",
      render: (value) => (
        <Typography variant="admin_txt1" className="text-admin_text">
          {formatDate(value)}
        </Typography>
      ),
    },
    {
      id: "user",
      title: "User",
      headerClassName: "w-[200px]",
      render: (value, item) => {
        const user = item.user
        if (!user) return <Typography variant="admin_txt2_dim">Unknown</Typography>
        return (
          <div
            className="flex flex-col cursor-pointer hover:opacity-80"
            onClick={() => setUserDetailsModal({ isOpen: true, user })}
          >
            <Typography variant="admin_txt1" className="font-semibold text-admin_primary">
              {user.name || "Unknown User"}
            </Typography>
            <Typography variant="admin_txt2_dim">
              {user.email || "No email"}
            </Typography>
          </div>
        )
      },
    },
    {
      id: "product_name",
      title: "Product",
      headerClassName: "w-[200px]",
      render: (value, item) => (
        <Typography
          variant="admin_txt1"
          className="text-admin_primary font-semibold cursor-pointer hover:underline"
          onClick={() => handleProductClick(item.product_id)}
        >
          {value}
        </Typography>
      ),
    },
    {
      id: "category",
      title: "Category",
      headerClassName: "w-[280px]",
      render: (value) => (
        <Typography variant="admin_txt1" className="text-admin_text">
          {value || "N/A"}
        </Typography>
      ),
    },
    {
      id: "user_notes",
      title: "Notes",
      headerClassName: "flex-1",
      render: (value) => {
        if (!value) {
          return <Typography variant="admin_txt2_dim" className="italic">No notes</Typography>
        }
        return (
          <Typography variant="admin_txt2" className="text-admin_text truncate" title={value}>
            {value}
          </Typography>
        )
      },
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-[80px] text-center",
      render: (value, item) => (
        <div className="flex items-center justify-center">
          <Button
            variant="admin_icon"
            className="text-admin_text_secondary hover:text-admin_error"
            onClick={() => handleDelete(item)}
          >
            <TrashIcon className="w-5 h-5" />
          </Button>
        </div>
      ),
    },
  ]

  const headerControls = (
    <div className="w-full flex items-center gap-3 -mt-4">
      <DropDownSelect
        variant="admin"
        options={categoryOptions}
        multiSelection
        searchEnabled
        fixedWidth="260px"
        placeholder="Filter by category"
        showCheckbox={false}
        value={categoryFilter}
        onChange={(value) => {
          setCategoryFilter(value)
          setRefreshKey((prev) => prev + 1)
        }}
      />

      <DropDownSelect
        variant="admin"
        searchEnabled
        useExternalSearch
        enableResetButton
        showCheckbox={false}
        apiMethod={async (params) => {
          if (!params.searchTerm || params.searchTerm.length < 2) {
            return { data: [] }
          }

          const response = await UserPlatformAPI.SearchUsers(params.searchTerm)
          return response
        }}
        onDataLoaded={(rawData) => {
          return rawData.map(user => ({
            _id: user._id,
            name: user.name || 'Unknown User',
            email: user.email || 'No email',
            value: user._id,
            label: `${user.name || 'Unknown User'} (${user.email || 'No email'})`,
            ...user
          }))
        }}
        searchField="name email"
        valueField="_id"
        nameField="label"
        fixedWidth="260px"
        placeholder="Filter by user"
        noDataMessage="Filter by user"
        value={userFilter}
        onChange={(value) => {
          setUserFilter(value)
          setRefreshKey((prev) => prev + 1)
        }}
        onValueItemRender={(user) => {
          if (!user) return "Filter by user"
          return (
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0">
                {user.avatar ? (
                  <img src={url(user.avatar)} alt={user.name} className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <span className="text-white font-medium text-xs">
                    {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="font-medium text-admin_text truncate">
                  {user.name || 'Unknown User'}
                </div>
                <div className="text-xs text-admin_text_secondary truncate">
                  {user.email || 'No email'}
                </div>
              </div>
            </div>
          )
        }}
        onItemRender={(user) => (
          <div className="flex items-center gap-3 py-2">
            <div className="w-8 h-8 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0">
              {user.avatar ? (
                <img src={url(user.avatar)} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <span className="text-white font-medium">
                  {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-admin_text truncate">{user.name || 'Unknown User'}</div>
              <div className="text-sm text-admin_text_secondary truncate">{user.email || 'No email'}</div>
            </div>
          </div>
        )}
      />
    </div>
  )

  return (
    <div className="p-4">
      <div className="w-full flex items-center justify-between mb-5">
        <div className="ml-4">
          <Typography
            component="h1"
            variant="admin_h3"
          >
            Unsubscribe Feedback
          </Typography>
          <Typography
            variant="admin_txt2"
            className="text-admin_text_secondary mt-1"
          >
            Track and analyze why users are canceling their subscriptions
          </Typography>
        </div>

        <Button
          variant="admin_primary"
          onClick={handleOpenReasonsDialog}
        >
          <div className="flex items-center gap-2">
            <Cog6ToothIcon className="w-5 h-5" />
            Reasons
          </div>
        </Button>
      </div>

      <DataTable
        variant="admin_browser"
        preferredHeight="70vh"
        apiMethod={customGetMethod}
        headerControls={headerControls}
        itemHeight="h-[80px]"
        searchField="category product_id subscription_id user_notes"
        searchObject={{}}
        requestFields={{}}
        sort={{ "content.feedback_date": -1 }}
        localUserPreferencesKey="admin.store.feedback"
        renders={feedbackItemRender}
        refreshKey={refreshKey}
        enableSearch
        styleOverrides={{
          table: "w-full table-fixed",
          searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[250px] xl:w-[350px] h-[30px] px-2 py-1"
        }}
        noData={
          <Typography variant="admin_body">
            No feedback matching the search criteria
          </Typography>
        }
      />

      {showReasonsDialog && reasonsInstance && (
        <Modal
          isOpen={showReasonsDialog}
          onClose={() => {
            setShowReasonsDialog(false)
            setReasonsInstance(null)
          }}
          variant="admin"
          className="w-full sm:w-[500px] md:w-[700px]"
        >
          <ContentEditor
            contentType={getType("UnsubscribeReasons")}
            dataItem={reasonsInstance}
            hidePanel={true}
            showSearch={false}
            showCopyPaste={false}
            showExport={false}
            onClickSave={async (result) => {
              if (result) {
                setShowReasonsDialog(false)
                setReasonsInstance(null)
                await fetchReasons()

                showNotification({
                  message: "Reasons updated successfully",
                  className: "bg-admin_success text-admin_text_inv",
                })
              }
            }}
            onSave={async (content) => {
              const contentWithType = {
                ...content,
                type: "UnsubscribeReasons"
              }

              const response = await controller.saveContent({
                payload: {
                  ...reasonsInstance,
                  content: contentWithType
                }
              })

              if (response?.error) {
                showNotification({
                  message: "Failed to save reasons",
                  className: "bg-admin_error text-admin_text_inv",
                })
                return null
              }

              return response
            }}
          />
        </Modal>
      )}

      <StoreProductInfoDialog
        isOpen={showProductInfoDialog}
        onClose={() => setShowProductInfoDialog(false)}
        pid={selectedProductPid}
      />

      <UserInfoDrawer
        userObject={userDetailsModal.user}
        isOpen={userDetailsModal.isOpen}
        onClose={() => setUserDetailsModal({ isOpen: false, user: null })}
      />
    </div>
  )
}
